import { useEffect, useState } from "react";
import { Eye, UploadCloud, RotateCcw, Monitor, Smartphone, Sparkles } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";
import { ANIM_DEFAULTS, mergeAnim } from "@/anim/engine";

const EFFECT_META = [
  { key: "stacked_swipe", name: "Stacked Swipe Product Cards", desc: "Cards in the chosen section stack like a deck — visitors swipe/drag the top card to reveal the next.", targets: ["new-arrivals", "best-sellers"] },
  { key: "hover_reveal", name: "Product Image Hover / Second Image Reveal", desc: "Hovering a product card fades in its second image.", targets: ["all", "new-arrivals", "best-sellers"] },
  { key: "scroll_reveal", name: "Scroll Product Reveal", desc: "Cards gently rise and fade in as they enter the viewport while scrolling.", targets: ["all", "new-arrivals", "best-sellers", "reviews"] },
  { key: "tilt_3d", name: "3D Product Tilt", desc: "Product images tilt subtly in 3D following the cursor (desktop only).", targets: ["new-arrivals", "best-sellers", "all"] },
  { key: "magnetic_buttons", name: "Magnetic Add to Cart / Shop Now Buttons", desc: "CTA buttons subtly gravitate toward the cursor (desktop only).", targets: ["all_ctas", "hero", "mid_banner"] },
  { key: "text_reveal", name: "Heading / Text Reveal Animation", desc: "Section headings and overlines reveal smoothly on scroll.", targets: ["all", "hero", "sections"] },
  { key: "hero_parallax", name: "Floating / Parallax Hero Product", desc: "Hero banner image floats gently and drifts on scroll.", targets: ["hero"] },
];

const TARGET_LABELS = { all: "All homepage sections", "new-arrivals": "New Arrivals carousel", "best-sellers": "Best Sellers carousel", reviews: "Reviews section", all_ctas: "All homepage CTAs", hero: "Hero slider", mid_banner: "Mid banner strip", sections: "All sections except hero" };

function Toggle({ checked, onChange, testid, label }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} data-testid={testid}
      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${checked ? "bg-[#D4AF37]" : "bg-[#E5DEC9]"}`} aria-label={label}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${checked ? "left-[1.375rem]" : "left-0.5"}`} />
    </button>
  );
}

function Slider({ value, min, max, step, onChange, format, testid, label }) {
  return (
    <div>
      <div className="flex justify-between text-[10px] uppercase tracking-wider text-[#6E6D6A] font-semibold mb-1">
        <span>{label}</span><span className="text-[#8C7A5E]" data-testid={`${testid}-value`}>{format(value)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid} className="w-full accent-[#D4AF37]" aria-label={label} />
    </div>
  );
}

export default function AdminAnimations() {
  const [cfg, setCfg] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      const merged = mergeAnim(data.animations);
      setCfg(merged);
      setSaved(merged);
    }).catch((e) => toast.error(e.friendlyMessage));
  }, []);

  const setEff = (key, patch) => setCfg((c) => ({ ...c, [key]: { ...c[key], ...patch } }));
  const dirty = cfg && saved && JSON.stringify(cfg) !== JSON.stringify(saved);

  const preview = () => {
    localStorage.setItem("tq_anim_draft", JSON.stringify(cfg));
    window.open("/?anim_preview=1", "_blank");
    toast.info("Preview opened in a new tab — changes are NOT published yet");
  };

  const publish = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.put("/admin/settings", { animations: cfg });
      setSaved(cfg);
      toast.success("Homepage animations published");
    } catch (e) { toast.error(e.friendlyMessage); } finally { setBusy(false); }
  };

  const reset = () => { setCfg(mergeAnim(null)); toast.info("Defaults restored — publish to apply"); };

  if (!cfg) return <div className="h-64 bg-white animate-pulse max-w-4xl" />;

  return (
    <div className="max-w-4xl" data-testid="admin-animations">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-3xl flex items-center gap-2"><Sparkles size={24} className="text-[#C5A059]" /> Homepage Animations</h1>
          <p className="text-sm text-[#6E6D6A] mt-1">Premium motion effects for the storefront homepage. Defaults are subtle and performance-friendly; everything respects reduced-motion preferences.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={preview} data-testid="anim-preview-btn" className="tq-btn-outline !py-2.5"><Eye size={14} /> Preview</button>
          <button onClick={reset} data-testid="anim-reset-btn" className="tq-btn-outline !py-2.5"><RotateCcw size={14} /> Defaults</button>
          <button onClick={publish} disabled={busy || !dirty} data-testid="anim-publish-btn" className="tq-btn-dark !py-2.5">
            <UploadCloud size={14} /> {busy ? "Publishing…" : dirty ? "Publish Changes" : "Published"}
          </button>
        </div>
      </div>

      {dirty && (
        <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#8C7A5E] text-xs font-semibold uppercase tracking-wider px-4 py-2.5 mb-5" data-testid="anim-unsaved-note">
          Unpublished changes — use Preview to test, then Publish to make them live.
        </div>
      )}

      <div className="space-y-4">
        {EFFECT_META.map((meta) => {
          const e = cfg[meta.key];
          return (
            <div key={meta.key} className={`bg-white border p-5 transition-colors ${e.enabled ? "border-[#D4AF37]/60" : "border-[#E5DEC9]"}`} data-testid={`anim-card-${meta.key}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-sm">{meta.name}</p>
                  <p className="text-xs text-[#6E6D6A] mt-1 max-w-xl">{meta.desc}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] uppercase tracking-wider font-bold ${e.enabled ? "text-[#1A3C34]" : "text-[#9a938a]"}`}>{e.enabled ? "ON" : "OFF"}</span>
                  <Toggle checked={e.enabled} onChange={(v) => setEff(meta.key, { enabled: v })} testid={`anim-toggle-${meta.key}`} label={`Toggle ${meta.name}`} />
                </div>
              </div>
              <div className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-[#F4EFE6] ${!e.enabled ? "opacity-40 pointer-events-none" : ""}`}>
                <Slider value={e.speed} min={0.5} max={2} step={0.1} onChange={(v) => setEff(meta.key, { speed: v })} format={(v) => `${v.toFixed(1)}×`} testid={`anim-speed-${meta.key}`} label="Speed" />
                <Slider value={e.intensity} min={1} max={10} step={1} onChange={(v) => setEff(meta.key, { intensity: v })} format={(v) => v <= 3 ? `${v} · Subtle` : v <= 7 ? `${v} · Medium` : `${v} · Dramatic`} testid={`anim-intensity-${meta.key}`} label="Intensity" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#6E6D6A] font-semibold mb-2">Devices</p>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="checkbox" checked={e.desktop} onChange={(ev) => setEff(meta.key, { desktop: ev.target.checked })} data-testid={`anim-desktop-${meta.key}`} className="accent-[#D4AF37] w-4 h-4" />
                      <Monitor size={13} /> Desktop
                    </label>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="checkbox" checked={e.mobile} onChange={(ev) => setEff(meta.key, { mobile: ev.target.checked })} data-testid={`anim-mobile-${meta.key}`} className="accent-[#D4AF37] w-4 h-4" />
                      <Smartphone size={13} /> Mobile
                    </label>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#6E6D6A] font-semibold mb-1">Applies To</p>
                  <select value={e.target} onChange={(ev) => setEff(meta.key, { target: ev.target.value })} data-testid={`anim-target-${meta.key}`}
                    className="tq-input !py-2 !text-xs bg-white" disabled={meta.targets.length === 1}>
                    {meta.targets.map((t) => <option key={t} value={t}>{TARGET_LABELS[t] || t}</option>)}
                  </select>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
